import React from 'react';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { 
  Container, 
  AppBar, 
  Toolbar, 
  Typography, 
  Box,
  Paper,
  Button,
  Card,
  CardContent,
  CardActions
} from '@mui/material';
import { Warehouse, Build, Assignment, Layers, GraphicEq, AccountTree, MenuBook } from '@mui/icons-material';
import { Link } from 'react-router-dom';

const theme = createTheme({
  palette: {
    primary: { main: '#1976d2' },
    secondary: { main: '#dc004e' },
  },
});

function HomePage() {
  const steps = [
    {
      id: 'mui-basics',
      title: 'MUI Basics',
      description: 'Material-UI components testing',
      icon: <Build />,
      status: 'Done',
      color: 'success'
    },
    {
      id: 'react-hook-form',
      title: 'React Hook Form',
      description: 'Form management and validation',
      icon: <Assignment />,
      status: 'Current',
      color: 'primary'
    },
    {
      id: 'mui-advanced',
      title: 'MUI + RHF Advanced',
      description: 'Complex forms with MUI components',
      icon: <Layers />,
      status: 'Next',
      color: 'default'
    },
    {
      id: 'graphql-setup',
      title: 'GraphQL Setup',
      description: 'Apollo Client and basic queries',
      icon: <GraphicEq />,
      status: 'Upcoming',
      color: 'default'
    },
    {
      id: 'integration',
      title: 'Full Integration',
      description: 'Complete warehouse management system',
      icon: <AccountTree />,
      status: 'Final',
      color: 'default'
    }
  ];

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      
      <AppBar position="static">
        <Toolbar>
          <Warehouse sx={{ mr: 2 }} />
          <Typography variant="h6">
            Warehouse React Playground
          </Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ mt: 4 }}>
        
        <Paper sx={{ p: 4, mb: 4, textAlign: 'center' }}>
          <Typography variant="h3" gutterBottom>
            Learning Progress
          </Typography>
          <Typography variant="h6" color="text.secondary">
            React, MUI, React Hook Form, GraphQL
          </Typography>
        </Paper>

        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: '1fr 1fr 1fr' },
          gap: 3,
          mb: 4
        }}>
          {steps.map((step) => (
            <Card key={step.id} sx={{ height: '100%' }}>
              <CardContent sx={{ textAlign: 'center', pb: 1 }}>
                <Box sx={{ color: 'primary.main', mb: 2 }}>
                  {step.icon}
                </Box>
                <Typography variant="h6" gutterBottom>
                  {step.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {step.description}
                </Typography>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    px: 2, 
                    py: 0.5, 
                    borderRadius: 1, 
                    bgcolor: step.color === 'success' ? 'success.light' : 
                             step.color === 'primary' ? 'primary.light' : 'grey.200',
                    color: step.color === 'default' ? 'text.secondary' : 'white'
                  }}
                >
                  {step.status}
                </Typography>
              </CardContent>
              <CardActions sx={{ justifyContent: 'center', pt: 0, gap: 1 }}>
                <Button 
                  component={Link}
                  to={`/${step.id}`}
                  variant={step.color === 'primary' ? 'contained' : 'outlined'}
                  disabled={step.status === 'Upcoming' || step.status === 'Final'}
                >
                  {step.status === 'Done' ? 'Review' : 
                   step.status === 'Current' ? 'Start Learning' : 'Coming Soon'}
                </Button>
                {(step.id === 'mui-basics' || step.id === 'react-hook-form' || step.id === 'mui-advanced' || step.id === 'graphql-setup' || step.id === 'integration') && (
                  <Button 
                    component={Link}
                    to={
                      step.id === 'mui-basics' ? '/docs/mui-basics' :
                      step.id === 'react-hook-form' ? '/docs/react-hook-form' :
                      step.id === 'mui-advanced' ? '/docs/mui-rhf-advanced-1' :
                      step.id === 'graphql-setup' ? '/docs/graphql-basics-1' :
                      step.id === 'integration' ? '/docs/full-integration-1' : '#'
                    }
                    variant="text"
                    size="small"
                    startIcon={<MenuBook />}
                  >
                    Docs
                  </Button>
                )}
              </CardActions>
            </Card>
          ))}
        </Box>

        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Quick Navigation
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button 
              component={Link} 
              to="/mui-basics" 
              variant="outlined"
              startIcon={<Build />}
            >
              MUI Components
            </Button>
            <Button 
              component={Link} 
              to="/react-hook-form" 
              variant="contained"
              startIcon={<Assignment />}
            >
              Hook Form Practice
            </Button>
          </Box>
        </Paper>

      </Container>
    </ThemeProvider>
  );
}

export default HomePage;
